import _ from 'lodash';
import Element from './Element';
import * as members from '../privates/privateMembers';
import backgroundMediaUtils from '../privates/utils/backgroundMediaUtils';
import {validate} from '../privates/validations/validation';
import monitoring from '../privates/monitoring';
import {isNilOrEmptyString} from '../privates/utils';

function mergedCompDesign(compDesign, mediaData) {
    return _.assign({}, compDesign, {
        type: 'MediaContainerDesignData',
        background: _.assign({}, compDesign.background, {
            mediaRef: mediaData,
            type: 'BackgroundMedia'
        })
    });
}

/**
 * @class Background
 * @summary Provides functionality for background images in certain elements.
 * @memberof $w
 */
export default class Background extends Element {

    /**
     * @typedef $w.Background~BackgroundOptions
     * @summary An object used by the `background` property that contains the background options.
     * @property {external:String} src The file location of the background image or video.
     *  Setting the `src` property changes the
     *  displayed image or video to the image or video found at the new `src` value.
     *
     *  Getting the `src` property returns the
     *  location of the current image or video file.
     *
     *  Images can either be from the [Media Manager](https://support.wix.com/en/article/about-the-media-manager)
     *  or an external image from any web location. Videos must come from the
     *  [Media Manager](https://support.wix.com/en/article/about-the-media-manager).
     *
     *  The URL formats supported are:
     *
     *  + Images from the Media Manager:
     *    `wix:image://v1/<uri>/<filename>#originWidth=<width>&originHeight=<height>[&watermark=<watermark_manifest_string>]`
     *  + Videos from the Media Manager:
     *    `wix:video://v1/<video_uri>/<filename>#posterUri=<poster_uri>&posterWidth=<width>&posterHeight=<height>`
     *  + Images from the web:
     *    `http(s)://<image url>`
     * 
     * @snippet [Background-background_get.es6=Get the background information]
     * @snippet [Background-background_set_wix.es6=Set the background image to be an image from the Media Manager]
     * @snippet [Background-background_set_web.es6=Set the background image to be an image from the web]
     * @see [background](#background)
     */

    /**
     * @member background
     * @label property
     * @summary Gets an object containing information about the element's background.
     * @snippet [Background-background_get.es6=Get the background information]
     * @snippet [Background-background_set_wix.es6=Set the background image to be an image from the Media Manager]
     * @snippet [Background-background_set_web.es6=Set the background image to be an image from the web]
     * @type {$w.Background~BackgroundOptions}
     * @memberof $w.Background
     * @readonly
     */
    get background() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const type = members._type.get(this);
        return new WrappedBackgroundOptions(RMI, compId, type);
    }
}


class BackgroundOptions {
    constructor(RMI, compId, type) {
        members._RMI.set(this, RMI);
        members._compId.set(this, compId);
        members._type.set(this, type);
    }

    get src() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const mediaRef = _.get(RMI.getDesign(compId), 'background.mediaRef') || {};

        return backgroundMediaUtils.buildFullUrlFromMediaRef(mediaRef);
    }

    set src(value) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'src',
            dataToValidate: {src: value},
            compName: this.id
        })) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compDesign = RMI.getDesign(compId) || {};

        if(isNilOrEmptyString(value)){
            RMI.setDesign(compId, mergedCompDesign(compDesign, null));
            return;
        }

        const currentMediaRef = _.get(compDesign, 'background.mediaRef');
        const mediaData = backgroundMediaUtils.getMediaDataFromSrc(value);
        if (!mediaData) {
            return;
        }
        if (currentMediaRef && currentMediaRef.opacity) {
            _.assign(mediaData, {opacity: currentMediaRef.opacity});
        }

        backgroundMediaUtils.getFullMediaData(mediaData, (fullMediaRefData) => {
            if (!fullMediaRefData){return;}
            /*setDesignAsync with all media data*/
            const updatedCompDesign = RMI.getDesign(compId) || {};
            _.assign(updatedCompDesign.background.mediaRef, fullMediaRefData);
            RMI.setDesign(compId, updatedCompDesign);
        });
        RMI.setDesign(compId, mergedCompDesign(compDesign, mediaData));
    }
}

const WrappedBackgroundOptions = monitoring.monitorClass(BackgroundOptions, monitoring.getRavenContextForComponent);
